import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowDown, Server, Cloud, Building2, Download } from "lucide-react";
import { DEPLOY_QUESTIONS, GOV_DISCLAIMER_DEPLOY } from "../../governance/govData";
import { useGov } from "../../governance/GovContext";
import { savePdf } from "../../governance/govPdf";
import { Button } from "../ui/button";

const FLOW = ["Customer", "Chat interface", "Bifröst Relay", "Aegis Engine", "Organisation policy", "Allow / Warn / Redact / Block / Escalate", "Underlying model", "Heimdall oversight & evidence"];

function category(d) {
  if (d.infra === "Customer-controlled environment required" || d.sensitivity === "Highly sensitive regulated information") return { name: "Customer-Controlled", icon: Building2 };
  if (d.infra === "Private cloud preferred" || d.sensitivity === "Financial information" || d.residency === "Specific jurisdiction") return { name: "Private Cloud", icon: Cloud };
  return { name: "Hosted", icon: Server };
}

export default function DeploymentConfigurator() {
  const { deploy, setDeploy } = useGov();
  const navigate = useNavigate();
  const d = deploy || {};
  const set = (id, v) => setDeploy({ ...d, [id]: v });
  const answered = DEPLOY_QUESTIONS.filter((q) => d[q.id]).length;
  const cat = category(d);
  const Icon = cat.icon;

  const discuss = () => {
    const lines = DEPLOY_QUESTIONS.filter((q) => d[q.id]).map((q) => `- ${q.text} ${d[q.id]}`).join("\n");
    const message = `I'd like to discuss an indicative ${cat.name} architecture based on the configurator:\n${lines}`;
    navigate("/book-a-demo", { state: { enquiry: "Book a walkthrough", message } });
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="space-y-3">
        {DEPLOY_QUESTIONS.map((q) => (
          <div key={q.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
            <p className="text-sm text-foreground">{q.text}</p>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {q.options.map((o) => (
                <button key={o} onClick={() => set(q.id, o)} className={`px-3 py-1.5 rounded-lg text-sm border transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 ${d[q.id] === o ? "border-primary/50 bg-primary/12 text-foreground" : "border-white/10 text-muted-foreground hover:text-foreground hover:bg-white/5"}`}>{o}</button>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="lg:sticky lg:top-32 lg:self-start space-y-4">
        <div className="rounded-lg border border-white/10 bg-navy-900/60 p-6">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Indicative deployment</p>
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-sm font-semibold text-primary"><Icon className="h-4 w-4" /> {cat.name}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{answered}/{DEPLOY_QUESTIONS.length} questions answered</p>
          <div className="mt-4 space-y-1.5">
            {FLOW.map((n, i) => (
              <React.Fragment key={n}>
                <div className={`rounded-lg border px-3 py-2 text-sm ${i >= 2 && i <= 5 ? "border-primary/30 bg-primary/[0.06] text-foreground" : "border-white/10 bg-white/[0.02] text-muted-foreground"}`}>{n}</div>
                {i < FLOW.length - 1 && <div className="flex justify-center text-primary/50"><ArrowDown className="h-3.5 w-3.5" /></div>}
              </React.Fragment>
            ))}
          </div>
        </div>
        <p className="text-xs text-muted-foreground/80">{GOV_DISCLAIMER_DEPLOY}</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <Button onClick={discuss} className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90">Discuss this architecture <ArrowRight className="ml-1.5 h-4 w-4" /></Button>
          <Button variant="outline" disabled={answered === 0} onClick={() => savePdf.deploy(d)} data-testid="deploy-download-pdf" className="border-white/15 bg-transparent hover:bg-white/5 text-foreground disabled:opacity-40"><Download className="mr-1.5 h-4 w-4" /> Download PDF</Button>
        </div>
      </div>
    </div>
  );
}
